import { useEffect, useState } from "react";
import { fetchTourismForAdmin, saveTourismToApi } from "../../utils/contentApi";
import { safeJsonParse } from "../../utils/safeJson";

function createEmptyItem() {
  return {
    id: `tourism-${Date.now()}`,
    title: "",
    description: "",
    imageUrl: "",
    link: "",
    isActive: true,
  };
}

function TourismAdmin() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [infoMessage, setInfoMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadItems = async () => {
      setIsLoading(true);
      setErrorMessage("");

      const cached = localStorage.getItem("carpeDiemTourism");
      if (cached && isMounted) {
        const parsed = safeJsonParse(cached, []);
        if (Array.isArray(parsed)) {
          setItems(parsed);
        }
      }

      try {
        const remote = await fetchTourismForAdmin();
        if (!isMounted) {
          return;
        }

        setItems(remote);
        localStorage.setItem("carpeDiemTourism", JSON.stringify(remote));
      } catch (_error) {
        if (!isMounted) {
          return;
        }

        setErrorMessage(
          "Impossible de charger les éléments tourisme depuis le serveur.",
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadItems();

    return () => {
      isMounted = false;
    };
  }, []);

  const persistItems = async (nextItems, successMessage) => {
    setIsSaving(true);
    setErrorMessage("");
    setInfoMessage("");

    const safeNextItems = Array.isArray(nextItems) ? nextItems : [];

    setItems(safeNextItems);
    localStorage.setItem("carpeDiemTourism", JSON.stringify(safeNextItems));

    try {
      const persisted = await saveTourismToApi(safeNextItems);

      setItems(persisted);
      localStorage.setItem("carpeDiemTourism", JSON.stringify(persisted));
      window.dispatchEvent(new Event("carpe-content-changed"));
      setInfoMessage(successMessage);
      return { success: true };
    } catch (_error) {
      setErrorMessage(
        "La sauvegarde distante a échoué. Les données restent en cache local.",
      );
      return { success: false };
    } finally {
      setIsSaving(false);
    }
  };

  const openCreateForm = () => {
    setEditingItem(createEmptyItem());
    setShowForm(true);
    setErrorMessage("");
    setInfoMessage("");
  };

  const openEditForm = (item) => {
    setEditingItem({ ...item });
    setShowForm(true);
    setErrorMessage("");
    setInfoMessage("");
  };

  const closeForm = () => {
    setEditingItem(null);
    setShowForm(false);
  };

  const updateField = (field, value) => {
    setEditingItem((prev) => ({ ...prev, [field]: value }));
  };

  const saveItem = async () => {
    const title = String(editingItem?.title || "").trim();
    const imageUrl = String(editingItem?.imageUrl || "").trim();

    if (!title) {
      setErrorMessage("Le titre est obligatoire.");
      return;
    }

    if (!imageUrl) {
      setErrorMessage("L'URL de l'image est obligatoire.");
      return;
    }

    const normalizedItem = {
      ...editingItem,
      title,
      imageUrl,
      description: String(editingItem?.description || "").trim(),
      link: String(editingItem?.link || "").trim(),
      isActive: editingItem?.isActive !== false,
    };

    const existingIndex = items.findIndex(
      (item) => item.id === normalizedItem.id,
    );

    const nextItems = [...items];
    if (existingIndex === -1) {
      nextItems.push(normalizedItem);
    } else {
      nextItems[existingIndex] = normalizedItem;
    }

    const result = await persistItems(
      nextItems,
      "Élément tourisme enregistré.",
    );
    if (result.success) {
      closeForm();
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm("Supprimer cet élément tourisme ?")) {
      return;
    }

    const nextItems = items.filter((item) => item.id !== itemId);
    await persistItems(nextItems, "Élément tourisme supprimé.");
  };

  const toggleActive = async (itemId) => {
    const nextItems = items.map((item) =>
      item.id === itemId
        ? { ...item, isActive: item.isActive === false }
        : item,
    );

    await persistItems(nextItems, "Statut de publication mis à jour.");
  };

  const moveItem = async (itemId, direction) => {
    const currentIndex = items.findIndex((item) => item.id === itemId);
    const targetIndex = currentIndex + direction;

    if (currentIndex === -1 || targetIndex < 0 || targetIndex >= items.length) {
      return;
    }

    const nextItems = [...items];
    [nextItems[currentIndex], nextItems[targetIndex]] = [
      nextItems[targetIndex],
      nextItems[currentIndex],
    ];

    await persistItems(nextItems, "Ordre mis à jour.");
  };

  return (
    <div className="dashboard events-admin-page">
      <div className="dashboard-header">
        <h1>Gestion du Tourisme</h1>
        <p className="dashboard-subtitle">
          Gérez les activités et lieux touristiques affichés sur la page
          Tourisme.
        </p>
      </div>

      <div className="events-admin-toolbar">
        <button
          type="button"
          className="refresh-btn reservation-open-create-btn"
          onClick={openCreateForm}
          disabled={isSaving}
        >
          + Ajouter un élément
        </button>
      </div>

      {infoMessage ? (
        <p className="reservations-state success">{infoMessage}</p>
      ) : null}
      {errorMessage ? (
        <p className="reservations-state error">{errorMessage}</p>
      ) : null}

      {showForm && editingItem ? (
        <section className="reservation-settings-panel events-admin-form-panel">
          <div className="reservation-settings-header">
            <h2>
              {items.some((item) => item.id === editingItem.id)
                ? "Modifier"
                : "Créer"}{" "}
              un élément tourisme
            </h2>
          </div>

          <div className="events-admin-form-grid">
            <div className="form-group">
              <label htmlFor="tourism-title">Titre *</label>
              <input
                id="tourism-title"
                className="reservation-filter-input"
                type="text"
                value={editingItem.title}
                onChange={(event) => updateField("title", event.target.value)}
                maxLength={120}
              />
            </div>

            <div className="form-group">
              <label htmlFor="tourism-image">URL image *</label>
              <input
                id="tourism-image"
                className="reservation-filter-input"
                type="url"
                value={editingItem.imageUrl}
                onChange={(event) =>
                  updateField("imageUrl", event.target.value)
                }
                placeholder="https://..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="tourism-link">Lien externe</label>
              <input
                id="tourism-link"
                className="reservation-filter-input"
                type="url"
                value={editingItem.link || ""}
                onChange={(event) => updateField("link", event.target.value)}
                placeholder="https://..."
              />
            </div>

            <div className="form-group events-admin-form-switch">
              <label className="reservation-switch">
                <input
                  type="checkbox"
                  checked={editingItem.isActive !== false}
                  onChange={(event) =>
                    updateField("isActive", event.target.checked)
                  }
                />
                <span>Publier cet élément</span>
              </label>
            </div>

            <div className="form-group events-admin-form-full">
              <label htmlFor="tourism-description">Description</label>
              <textarea
                id="tourism-description"
                className="reservation-filter-input reservation-admin-message"
                value={editingItem.description}
                onChange={(event) =>
                  updateField("description", event.target.value)
                }
                rows="5"
                maxLength={1000}
              />
            </div>
          </div>

          <div className="reservation-settings-footer">
            <button
              type="button"
              className="refresh-btn"
              onClick={closeForm}
              disabled={isSaving}
            >
              Annuler
            </button>
            <button
              type="button"
              className="refresh-btn reservation-open-create-btn"
              onClick={saveItem}
              disabled={isSaving}
            >
              {isSaving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </section>
      ) : null}

      {isLoading ? (
        <p className="reservations-state">
          Chargement des éléments tourisme...
        </p>
      ) : items.length === 0 ? (
        <p className="reservations-state">
          Aucun élément tourisme pour le moment.
        </p>
      ) : (
        <div className="events-admin-list">
          {items.map((item, index) => (
            <article key={item.id} className="event-admin-card">
              <div className="event-admin-media">
                <img src={item.imageUrl} alt={item.title} loading="lazy" />
              </div>

              <div className="event-admin-content">
                <div className="event-admin-header">
                  <h3>{item.title}</h3>
                  <div className="event-admin-badges">
                    <span className="event-admin-type">Tourisme</span>
                    <span
                      className={`event-admin-badge${
                        item.isActive === false ? " off" : ""
                      }`}
                    >
                      {item.isActive === false ? "Masqué" : "Publié"}
                    </span>
                  </div>
                </div>

                {item.link ? (
                  <p className="event-admin-dates">
                    Lien:{" "}
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.link}
                    </a>
                  </p>
                ) : (
                  <p className="event-admin-dates">Aucun lien externe</p>
                )}

                <p className="event-admin-description">
                  {item.description || "Aucune description."}
                </p>

                <div className="event-admin-actions">
                  <button
                    type="button"
                    className="refresh-btn"
                    onClick={() => moveItem(item.id, -1)}
                    disabled={isSaving || index === 0}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="refresh-btn"
                    onClick={() => moveItem(item.id, 1)}
                    disabled={isSaving || index === items.length - 1}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="refresh-btn"
                    onClick={() => toggleActive(item.id)}
                    disabled={isSaving}
                  >
                    {item.isActive === false ? "Publier" : "Masquer"}
                  </button>
                  <button
                    type="button"
                    className="refresh-btn"
                    onClick={() => openEditForm(item)}
                    disabled={isSaving}
                  >
                    Modifier
                  </button>
                  <button
                    type="button"
                    className="reservation-delete-btn"
                    onClick={() => deleteItem(item.id)}
                    disabled={isSaving}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

export default TourismAdmin;
